import { MoreVertIcon, Pin } from '@tunasong/icons'
import styles from './entity-card.module.css'
import { canWriteEntity, dayjs, isScaffold } from '@tunasong/models'
import { graphHooks } from '@tunasong/graph-lib/react'
import { usePlugins } from '@tunasong/plugin-lib'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, EntityOrElement, Persisted } from '@tunasong/schemas'
import { entityTypeNames, isPersisted } from '@tunasong/schemas'
import type { HTMLAttributes, JSX, MouseEvent, ReactNode } from 'react'
import React, { useState } from 'react'
import { useDrop } from 'react-dnd'
import { ConfirmDialog } from '../dialog/index.js'
import { useUIExtensions } from '../extensions.hook.js'
import { VBox } from '../layout/vertical-box.js'
import { Skeleton, Typography } from '../primitives/index.js'
import UserAvatar from '../profile/user-avatar.js'
import { SketchBadge } from './sketch-badge.js'
import { dropOnCard } from './card-drop.js'
import { containerDropState, moveCaption, moveConfirmText, refuseCaption } from './container-drop.js'
import { CardParentSpace } from './card-parent-space.js'
import { EntityIcon } from './entity-icon.js'
import { metaSeparator } from './meta-separator.js'
import { IconButton, Menu, MenuTrigger } from '../primitives/index.js'

/**
 * The card's media slot, which doubles as a drop target for adding a child
 * entity.
 *
 * This lives in its own component so that `useDrop` runs ONLY when the media
 * slot actually renders. react-dnd's `useDrop` throws
 * `Invariant Violation: Expected drag drop context` when no `DndProvider` is
 * mounted above it, so calling it at the top of EntityCard made react-dnd a
 * hard requirement of every card — for a drop target that is attached to the
 * DOM only when `cardMedia` or `children` is passed. Of the six EntityCard
 * call sites only the image plugin passes either; EntityGrid, the call path in
 * the production stack trace, passes neither. GH #1740.
 */
const CardMediaDropTarget: React.FC<{
  entity: Persisted<Entity>
  hasCardImage: boolean
  onClick?: HTMLAttributes<HTMLDivElement>['onClick']
  children?: React.ReactNode
}> = ({ entity, hasCardImage, onClick, children }) => {
  const { userId } = useCurrentUser()
  /** Awaits the move and shows "Couldn't move …" when it fails (GH #2549), as Move and the list's drag-and-drop do */
  const { moveEntity } = graphHooks.useEntityUtil()
  const plugins = usePlugins('all')
  /** The drop waiting for the confirm, as on a list row (GH #3164 §3, decision 1) */
  const [pending, setPending] = useState<Persisted<Entity> | null>(null)
  const [moving, setMoving] = useState(false)

  /** The list's kind B rule: only a container that takes the dragged type, and the user can write to both */
  const dropState = (item: EntityOrElement | null | undefined) =>
    isPersisted(item)
      ? containerDropState({
          source: item as Persisted<Entity>,
          target: entity,
          plugins,
          /** As on a list row: an unsaved entity has no owner yet; a saved one needs write access */
          canWrite: candidate => !isPersisted(candidate) || canWriteEntity(userId, candidate),
        })
      : 'none'

  const [{ hoverState = 'none' }, dropRef] = useDrop({
    accept: entityTypeNames as never,
    drop: (item: EntityOrElement) => {
      if (dropState(item) === 'accept') {
        setPending(item as Persisted<Entity>)
      }
    },
    canDrop: (item: EntityOrElement) => dropState(item) === 'accept',
    collect: monitor => ({
      hoverState: monitor.isOver({ shallow: true }) ? dropState(monitor.getItem<EntityOrElement | null>()) : 'none',
    }),
  })

  const handleConfirm = async () => {
    if (!pending) {
      return
    }
    setMoving(true)
    await dropOnCard(pending, entity, moveEntity)
    setMoving(false)
    setPending(null)
  }

  const mediaClasses = [
    styles.mediaContent,
    hoverState === 'accept' ? styles.dropAccept : undefined,
    hoverState === 'refuse' ? styles.dropRefuse : undefined,
    hasCardImage ? styles.mediaContentOverlay : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <div
        className={mediaClasses}
        ref={dropRef as never}
        onClick={onClick}
        data-drop={hoverState === 'none' ? undefined : hoverState}
        aria-disabled={hoverState === 'refuse' ? 'true' : undefined}
      >
        {children}
        {hoverState === 'none' ? null : (
          <span className={styles.dropCaption} aria-hidden="true">
            {hoverState === 'accept' ? moveCaption(entity) : refuseCaption(entity)}
          </span>
        )}
      </div>
      <ConfirmDialog
        open={Boolean(pending)}
        busy={moving}
        title="Confirm move"
        text={pending ? moveConfirmText(pending, entity) : ''}
        onCancel={() => setPending(null)}
        onConfirm={() => void handleConfirm()}
      />
    </>
  )
}

export interface EntityCardProps<TElement extends Entity = Entity> extends HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>
  /** @todo these actions should have a onClose handler */
  actions?: JSX.Element
  /** If not specified display a skeleton */
  entity?: TElement

  size?: 'small' | 'medium' | 'large' | 'tiny'

  cardMedia?: JSX.Element
  backgroundImage?: string

  /** Show the entity header. @default true */
  showHeader?: boolean
  /** Show menu. @default true */
  showMenu?: boolean
  /** Show secondary text. @default true */
  showSecondary?: boolean
  /** Top action button */
  headerAction?: JSX.Element | null
  /** Optional prefix added verbatim to the card's accessible name (e.g., "Pinned: "). */
  itemAriaLabelPrefix?: string
  /** Phase 2 dashboard: gate the last-editor avatar/dot. Defaults to true. */
  showLastEditor?: boolean
  /** Recently-updated grid: show a muted parent-space chip in the secondary line. @default false */
  showParentSpace?: boolean
  /** Show a pin glyph before the title, and name the card "Pinned: <name>" (Home Continue). @default false */
  pinned?: boolean
  /** A line of metadata under the title (Home Continue: a song's key, tempo and length). */
  meta?: ReactNode
  /**
   * An action beside the card's open target, never inside it (stretched-link pattern: the open target covers
   * the card, the action sits above it). Home Continue: Perform.
   */
  trailingAction?: ReactNode
  /**
   * Small screens: `meta` joins the secondary line instead of adding its own, keeping the row short, and takes the
   * parent space's place there. @default false
   */
  compact?: boolean
}

export const EntityCard = (props: EntityCardProps) => {
  const {
    headerAction,
    cardMedia = null,
    showMenu = true,
    showHeader = true,
    showSecondary = true,
    actions,
    entity,
    backgroundImage: cardImage,
    children,
    ref,
    onClick,
    size,
    style: callerStyle,
    itemAriaLabelPrefix,
    showLastEditor = true,
    showParentSpace = false,
    pinned = false,
    meta,
    trailingAction,
    compact = false,
    ...restProps
  } = props

  const uiExtensions = useUIExtensions()

  const me = useCurrentUser()
  const hasExternalOwner = isPersisted(entity) ? entity.userId !== me.userId : false

  // Phase 2 dashboard pivot: surface the *last editor* alongside the owner — but
  // only when somebody ELSE edited last. A self-edit indicator is pure noise: in
  // "Continue working" / "Pinned" (your own recent items) every card would carry
  // one. Showing the avatar only for others' edits makes its presence meaningful
  // ("someone else touched this"). Nothing renders for your own edits, or when
  // the section opts out via `showLastEditor={false}` (solo-user path).
  const lastEditorId = isPersisted(entity) ? (entity.lastEditedBy ?? entity.userId) : undefined
  const isSelfEdit = lastEditorId === me.userId
  const showLastEditorAvatar = showLastEditor && lastEditorId !== undefined && !isSelfEdit

  /**
   * Canonical Menu composition (GH #1496): the actions button is the MenuTrigger.
   * The ItemMenu is expensive, so it mounts on first open and then stays mounted
   * so dialogs opened from its items outlive the menu.
   */
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [menuMounted, setMenuMounted] = React.useState(false)
  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open)
    if (open) {
      setMenuMounted(true)
    }
  }
  const handleCloseActions = () => setMenuOpen(false)
  /**
   * The card header is itself clickable. Clicks on the trigger, and on menu items
   * (React propagates portal events through the component tree), must not open
   * the entity — stopped once at the header-actions container.
   */
  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()
  const ItemMenu = showMenu ? uiExtensions.entityList?.ItemMenu : undefined
  const actionsLabel = entity?.name ? `Actions for ${entity.name}` : 'Actions'
  const menuButton = (
    <IconButton className={styles.menuButton} color="inherit" aria-label={actionsLabel}>
      <MoreVertIcon />
    </IconButton>
  )

  const handleContext = (ev: MouseEvent) => {
    ev.preventDefault()
  }

  // Shared min-height between the skeleton and the resolved card so swapping
  // from loading state to real content does not cause vertical layout shift
  // (Nielsen #1 — visibility of system status; jank works against trust).
  const cardMinHeight = showSecondary ? (size === 'large' ? 100 : 68) : size === 'large' ? 64 : 40

  if (!isPersisted(entity)) {
    return (
      <div className={styles.card} style={{ minHeight: cardMinHeight }}>
        <div className={styles.header} style={{ padding: size === 'large' ? 16 : 8 }}>
          <Skeleton variant="circular" width={size === 'large' ? 32 : 24} height={size === 'large' ? 32 : 24} />
          <div className={styles.headerContent}>
            <Skeleton variant="rounded" width="60%" height={size === 'large' ? 32 : 28} />
            {showSecondary ? <Skeleton variant="rounded" width="40%" height={size === 'large' ? 20 : 14} /> : null}
          </div>
        </div>
      </div>
    )
  }

  const subheader = entity ? `${dayjs(entity.updatedAt).fromNow()} ` : ''
  /**
   * Status, not provenance: a kept image is the user's and carries no badge in any list.
   * Labelling work the musician decided to keep is what the research says devalues it.
   */
  const showSketchBadge = isScaffold(entity)

  const hAction = headerAction ? (
    headerAction
  ) : headerAction !== null ? (
    <VBox className={styles.headerActions} onClick={stopPropagation}>
      {hasExternalOwner && <UserAvatar userId={entity.userId} style={{ opacity: 0.3 }} size="small" />}

      {ItemMenu ? (
        <Menu open={menuOpen} onOpenChange={handleMenuOpenChange}>
          <MenuTrigger render={menuButton} />
          {menuMounted ? <ItemMenu entity={entity} onClose={handleCloseActions} /> : null}
        </Menu>
      ) : (
        menuButton
      )}
    </VBox>
  ) : null

  const hasTrailingAction = Boolean(trailingAction)
  const headerClasses = [
    styles.header,
    cardImage ? styles.headerOverlay : undefined,
    hasTrailingAction ? styles.stretchedHeader : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  const hasMeta = meta !== undefined && meta !== null && meta !== ''
  const showSecondaryLine = showSecondary && Boolean(subheader)
  const inlineMeta = hasMeta && compact && showSecondaryLine
  const lastEditorAvatar =
    showLastEditorAvatar && lastEditorId ? <UserAvatar userId={lastEditorId} size="tiny" lastEditedContext /> : null
  /** With an action, the avatar sits beside it; otherwise it keeps its corner */
  const trailingSlot = hasTrailingAction ? (
    <div className={styles.trailing} data-testid="card-trailing">
      {lastEditorAvatar}
      {trailingAction}
    </div>
  ) : lastEditorAvatar ? (
    <div className={styles.lastEditorSlot}>{lastEditorAvatar}</div>
  ) : null

  return (
    <div
      className={[
        styles.card,
        cardImage ? styles.hasImage : undefined,
        hasTrailingAction ? styles.withTrailing : undefined,
      ]
        .filter(Boolean)
        .join(' ')}
      ref={ref}
      onContextMenu={handleContext}
      style={{ minHeight: cardMinHeight, ...callerStyle }}
      {...restProps}
    >
      {cardImage ? <div className={styles.backgroundImage} style={{ backgroundImage: `url(${cardImage})` }} /> : null}

      {showHeader ? (
        // A11Y: header acts as a click target to open the entity. We use a div
        // with role="button" rather than a real <button> because the card has
        // grid/flex layout and contains other focusable children (the MoreVert
        // IconButton) — nesting a button inside a button would be invalid.
        // Keyboard handlers make this reachable for keyboard + screen-reader users.
        <div
          className={headerClasses}
          style={{ padding: size === 'large' ? 16 : 8 }}
          onClick={onClick}
          onKeyDown={
            onClick
              ? ev => {
                  // Only the header's own keys: Enter/Space on a nested control
                  // (the actions MenuTrigger) must activate that control.
                  if (ev.target !== ev.currentTarget) {
                    return
                  }
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault()
                    onClick(ev as unknown as React.MouseEvent<HTMLDivElement>)
                  }
                }
              : undefined
          }
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
          data-entity-id={entity.id}
          aria-label={
            onClick ? `${itemAriaLabelPrefix ?? (pinned ? 'Pinned: ' : 'Open ')}${entity.name ?? 'item'}` : undefined
          }
        >
          <EntityIcon entity={entity} size={size} />
          <div className={styles.headerContent}>
            <div className={styles.titleRow}>
              {pinned ? (
                <span role="img" aria-label="Pinned" className={styles.pinGlyph}>
                  <Pin size={14} aria-hidden="true" />
                </span>
              ) : null}
              <Typography variant={size === 'large' ? 'h6' : 'subtitle1'} component="div" className={styles.ellipsis}>
                {entity.name}
              </Typography>
            </div>
            {hasMeta && !inlineMeta ? (
              <Typography
                variant={size === 'large' ? 'body2' : 'caption'}
                component="div"
                className={[styles.meta, styles.ellipsis].join(' ')}
                data-testid="card-meta"
              >
                {meta}
              </Typography>
            ) : null}
            {showSecondaryLine ? (
              <div className={styles.secondaryLine} data-testid="card-secondary">
                {/* CardParentSpace owns its trailing separator: it emits the space name and a
                 * " · " only when the entity has an ancestor space, so a top-level entity shows
                 * just the time with no orphan leading separator. */}
                {/* Compact rows with meta drop the space: one truncated segment per line at most,
                 * and the meta is the news. */}
                {showParentSpace && !inlineMeta ? <CardParentSpace entity={entity} /> : null}
                {inlineMeta ? (
                  <>
                    <Typography
                      variant="caption"
                      component="span"
                      className={styles.inlineMeta}
                      data-testid="card-meta"
                    >
                      {meta}
                    </Typography>
                    <Typography variant="caption" component="span" aria-hidden="true">
                      {metaSeparator}
                    </Typography>
                  </>
                ) : null}
                <Typography
                  variant={size === 'large' ? 'body2' : 'caption'}
                  component="span"
                  className={styles.secondaryTime}
                >
                  {subheader}
                </Typography>
              </div>
            ) : null}
          </div>
          {showSketchBadge ? <SketchBadge size="sm" /> : null}
          {hAction}
        </div>
      ) : null}

      {(cardMedia || children) && (
        <CardMediaDropTarget entity={entity} hasCardImage={Boolean(cardImage)} onClick={onClick}>
          {cardMedia}
          {children}
        </CardMediaDropTarget>
      )}
      {trailingSlot}
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  )
}

export default EntityCard
